<script setup>
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { sendBradleyReport } from '../services/bradley-ai.service.js'

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
  messageId: {
    type: String,
    default: null,
  },
  existingReport: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['close', 'success'])

const { t } = useI18n()

const reports = ref([
  { name: () => t('newBradley.reportModal.violenceThreats'), key: 'A', value: 'Violence or threats' },
  { name: () => t('newBradley.reportModal.selfHarmSuicide'), key: 'B', value: 'Self-harm or suicide' },
  { name: () => t('newBradley.reportModal.harassmentBullying'), key: 'C', value: 'Harassment or bullying' },
  { name: () => t('newBradley.reportModal.hateSpeechDiscrimination'), key: 'D', value: 'Hate speech or discrimination' },
  { name: () => t('newBradley.reportModal.sexualExploitationAbuse'), key: 'F', value: 'Sexual exploitation or abuse' },
  { name: () => t('newBradley.reportModal.misinformationHarmfulAdvice'), key: 'G', value: 'Misinformation or harmful advice' },
  { name: () => t('newBradley.reportModal.exploitationCriminalActivity'), key: 'H', value: 'Exploitation or criminal activity' },
  { name: () => t('newBradley.reportModal.spamMaliciousContent'), key: 'I', value: 'Spam or malicious content' },
  { name: () => t('newBradley.reportModal.somethingElse'), key: 'J', value: 'Something else' },
])
const selectedReports = ref([])
const details = ref('')
const isSubmitting = ref(false)

const step = ref(1)

// Pre-fill when message already has a report
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      if (props.existingReport && (props.existingReport.categories?.length || props.existingReport.description)) {
        selectedReports.value = Array.isArray(props.existingReport.categories)
          ? [...props.existingReport.categories]
          : []
        details.value = props.existingReport.description ?? ''
      }
      else {
        selectedReports.value = []
        details.value = ''
      }
    }
  },
)

function handleNext() {
  step.value = 2
}
function handleBack() {
  step.value = 1
}

function handleClose() {
  step.value = 1
  selectedReports.value = []
  details.value = ''
  emit('close')
}

async function handleSubmit() {
  const conversationId = props.conversationId
  const messageId = props.messageId
  if (!conversationId || !messageId || isSubmitting.value) {
    return
  }

  const categories = selectedReports.value

  try {
    isSubmitting.value = true
    await sendBradleyReport(conversationId, {
      message_id: messageId,
      categories,
      description: details.value || undefined,
    })

    emit('success')
    handleClose()
  }
  catch (error) {
    console.error('Failed to send report:', error)
  }
  finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0"
      enter-to-class="transform opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        @click.self="handleClose"
      >
        <div class="inset-0 z-[-1] bg-black opacity-40 w-full h-full absolute top-0 left-0" />
        <div class="flex w-full max-w-[590px] max-h-[480px] flex-col overflow-hidden rounded-2xl bg-white">
          <div class="flex items-center justify-between px-6 py-4">
            <h2 class="font-inter text-xl font-bold leading-none tracking-[0.005em]">
              {{ $t('newBradley.reportModal.title') }}
            </h2>
            <button
              class="rounded-lg p-2 transition-colors hover:bg-gray-100"
              @click="handleClose"
            >
              <i class="pi pi-times text-lg text-gray-600" />
            </button>
          </div>

          <div
            v-if="step === 1"
            class="flex w-full max-w-[590px] max-h-[480px] flex-col overflow-hidden rounded-2xl bg-white"
          >
            <div class="px-6 py-3">
              <p class="text-sm text-gray-600">
                {{ $t('newBradley.reportModal.whyReporting') }}
              </p>
            </div>

            <div class="flex-1 overflow-y-auto px-6 py-4">
              <div
                v-if="loading"
                class="flex flex-col items-center justify-center gap-3 py-12 text-gray-500"
              >
                <i class="pi pi-spinner animate-spin text-2xl" />
                <p class="text-sm">
                  {{ $t('newBradley.reportModal.loading') }}
                </p>
              </div>

              <div
                v-for="report of reports"
                :key="report.key"
                class="flex items-center gap-3 h-10"
              >
                <input
                  :id="report.key"
                  v-model="selectedReports"
                  type="checkbox"
                  :value="report.value"
                  class="w-4 h-4 text-blue-600 bg-gray-100 rounded cursor-pointer border-dss-surface-300"
                >
                <label
                  :for="report.key"
                  class="cursor-pointer text-sm text-gray-700"
                >{{ typeof report.name === 'function' ? report.name() : report.name }}</label>
              </div>
            </div>

            <div class="px-6 py-4 border-t border-dss-surface-200 flex justify-end gap-3">
              <button
                class="py-2 px-3 border border-[#B0B2DA] text-sm hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors cursor-pointer"
                @click="handleClose"
              >
                {{ $t('newBradley.reportModal.cancel') }}
              </button>
              <button
                class="py-2 px-3 bg-dss-primary-500 hover:bg-dss-primary-600 font-hellix font-medium text-sm leading-none text-white rounded-lg transition-colors cursor-pointer"
                @click="handleNext"
              >
                {{ $t('newBradley.reportModal.next') }}
              </button>
            </div>
          </div>

          <div
            v-if="step === 2"
            class="flex w-full max-w-[590px] max-h-[480px] flex-col overflow-hidden rounded-2xl bg-white"
          >
            <div class="px-6 py-3">
              <p class="text-sm text-gray-600">
                {{ $t('newBradley.reportModal.tellUsMore') }}
              </p>
            </div>

            <div class="flex-1 overflow-y-auto px-6 py-4">
              <textarea
                v-model="details"
                rows="6"
                class="w-full p-3 border border-dss-surface-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                :placeholder="$t('newBradley.reportModal.typeMessage')"
              />
            </div>

            <div class="px-6 py-4 border-t border-dss-surface-200 flex justify-end gap-3">
              <button
                class="py-2 px-3 border border-[#B0B2DA] text-sm hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors cursor-pointer"
                @click="handleBack"
              >
                {{ $t('newBradley.reportModal.back') }}
              </button>
              <button
                class="py-2 px-3 bg-dss-primary-500 hover:bg-dss-primary-600 font-hellix font-medium text-sm leading-none text-white rounded-lg transition-colors cursor-pointer"
                :disabled="isSubmitting || !messageId"
                @click="handleSubmit"
              >
                <i
                  v-if="isSubmitting"
                  class="pi pi-spinner animate-spin"
                />
                {{ isSubmitting ? $t('newBradley.reportModal.submitting') : $t('newBradley.reportModal.submit') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>
